import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  { label: "Get a Quote", to: "/quote" },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close mobile menu on route change and prevent background scrolling when open
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
    return () => document.body.classList.remove("overflow-hidden");
  }, [mobileOpen]);

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border" role="banner">
      <div className="container flex h-20 md:h-28 items-center justify-between">
        <Link to="/" className="flex items-center py-1" aria-label="DH Cleaning Services — Home">
          <img src="/images/logo-dh.png" alt="DH Cleaning Services logo" className="h-16 sm:h-20 md:h-24 w-auto object-contain" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={cn(
                "px-4 py-2 rounded-md text-sm font-medium transition-colors",
                location.pathname === link.to
                  ? "text-primary bg-secondary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              )}
              aria-current={location.pathname === link.to ? "page" : undefined}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a href="tel:+61450569050" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
            <Phone className="h-4 w-4" aria-hidden="true" />
            <span>0450 569 050</span>
          </a>
          <Button asChild>
            <Link to="/booking">Book Now</Link>
          </Button>
        </div>

        {/* Mobile actions */}
        <div className="flex md:hidden items-center gap-1">
          <a
            href="tel:+61450569050"
            className="p-2 rounded-md hover:bg-muted text-primary"
            aria-label="Call 0450 569 050"
          >
            <Phone className="h-5 w-5" aria-hidden="true" />
          </a>
          <button
            className="p-2 rounded-md hover:bg-muted"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Backdrop: tapping outside the menu closes it */}
      <div
        className={cn(
          "fixed inset-0 top-20 md:top-28 bg-black/25 z-40 md:hidden transition-opacity duration-200",
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        className={cn(
          "md:hidden absolute top-full left-0 right-0 z-50 border-t border-border bg-background shadow-xl transition-all duration-200 ease-out overflow-hidden",
          mobileOpen ? "max-h-96 opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
        )}
        role="dialog"
        aria-label="Mobile navigation"
        aria-hidden={!mobileOpen}
      >
        <nav className="container flex flex-col gap-1 py-3" aria-label="Mobile navigation">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors border-l-4",
                  isActive
                    ? "text-primary bg-secondary border-primary font-semibold"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted border-transparent"
                )}
                aria-current={isActive ? "page" : undefined}
              >
                {isActive && <span className="sr-only">Current page:</span>}
                {link.label}
              </Link>
            );
          })}
          <div className="px-4 pt-3">
            <Button asChild className="w-full">
              <Link to="/booking" onClick={() => setMobileOpen(false)}>Book Now</Link>
            </Button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
